import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { useAdminUsers, type AdminUsersParams } from "@/hooks/use-admin-users";
import { useAdminOrgs } from "@/hooks/use-admin-orgs";
import { useDebounce } from "@/hooks/use-debounce";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { RoleBadge } from "@/components/role-badge";
import { StatusBadge } from "@/components/status-badge";
import { formatDate } from "@njbsoft/utils";

type UserSummary = AdminComponents["schemas"]["AdminUserSummary"];

export const Route = createFileRoute("/_admin/users/")({
  component: UsersListPage,
});

const PAGE_SIZE = 20;

const columnHelper = createColumnHelper<UserSummary>();

function UsersListPage() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [orgFilter, setOrgFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  const debouncedSearch = useDebounce(search, 300);
  const { data: orgsData } = useAdminOrgs({ limit: 100 });

  // Build query params
  const params: AdminUsersParams = useMemo(() => {
    const p: AdminUsersParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (debouncedSearch) p.search = debouncedSearch;
    if (roleFilter !== "all") p.role = roleFilter as AdminUsersParams["role"];
    if (statusFilter !== "all") p.status = statusFilter as AdminUsersParams["status"];
    if (orgFilter) p.org_id = orgFilter;
    if (sorting.length > 0 && sorting[0]) {
      p.sort = sorting[0].id as AdminUsersParams["sort"];
      p.order = sorting[0].desc ? "desc" : "asc";
    }
    return p;
  }, [debouncedSearch, roleFilter, statusFilter, orgFilter, page, sorting]);

  const { data, isLoading } = useAdminUsers(params);

  const columns = useMemo(
    () => [
      columnHelper.accessor("full_name", {
        header: "Name",
        enableSorting: true,
      }),
      columnHelper.accessor("email", {
        header: "Email",
        enableSorting: true,
      }),
      columnHelper.accessor("role", {
        header: "Role",
        enableSorting: true,
        cell: (info) => <RoleBadge role={info.getValue()} />,
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell: (info) => {
          const user = info.row.original;
          const status = user.deleted_at ? "inactive" : "active";
          return <StatusBadge status={status} type="user" />;
        },
      }),
      columnHelper.display({
        id: "mfa",
        header: "MFA",
        cell: (info) => {
          const mfaEnabled = info.row.original.mfa_enabled;
          return mfaEnabled ? (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
              Enabled
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">
              Disabled
            </span>
          );
        },
      }),
      columnHelper.accessor("org_count", {
        header: "Orgs",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("created_at", {
        header: "Created",
        enableSorting: true,
        cell: (info) => formatDate(info.getValue()),
      }),
    ],
    [],
  );

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  function handleRowClick(user: UserSummary) {
    void navigate({ to: "/users/$userId", params: { userId: user.id } });
  }

  // Reset page when filters change
  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function handleRoleChange(value: string) {
    setRoleFilter(value);
    setPage(1);
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Users</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />

        <select
          value={roleFilter}
          onChange={(e) => handleRoleChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Roles</option>
          <option value="tester">Tester</option>
          <option value="admin">Admin</option>
          <option value="supervisor">Supervisor</option>
          <option value="super_admin">Super Admin</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          value={orgFilter}
          onChange={(e) => {
            setOrgFilter(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">All Orgs</option>
          {orgsData?.orgs?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.users ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No users found"
        onRowClick={handleRowClick}
      />

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
